import { shareOfCents } from './money.ts';
import type { ExpenseCategory, TaxLines } from './schemas.ts';

/**
 * Kalusteet ja laitteet: enintään tämän hintainen hankinta vähennetään kerralla, kalliimmista
 * 25 % vuodessa esinekohtaisesti. Kun esineen poistamaton osa on verovuoden alussa enintään
 * saman verran, loppu vähennetään kerralla. (Verohallinnon ohje: Vuokratulojen verotus,
 * kalusteet ja kodinkoneet.)
 */
export const FURNITURE_IMMEDIATE_LIMIT_CENTS = 120_000;
const FURNITURE_RATE = 0.25;
/** Perusparannus: tasapoisto 10 vuodessa maksuvuodesta alkaen. */
export const IMPROVEMENT_YEARS = 10;

export interface TaxExpense {
  year: number;
  category: ExpenseCategory;
  amountCents: number;
  /** Erittelyä varten (kalusteluettelo, perusparannukset). */
  description?: string;
}

/** Ennen Vuokranttia alkaneet poistot (koko asunnon summina). */
export interface DepreciationOpeningValues {
  /** Aiemmat perusparannukset: alkuperäinen hankintameno ja maksuvuosi. */
  improvements: Array<{ year: number; amountCents: number; description?: string }>;
  /** Kalusteiden poistamaton hankintameno vuoden alussa. Sisältää aiemmat kalustehankinnat. */
  furnitureBalance: { year: number; amountCents: number } | null;
}

export interface TaxInput {
  year: number;
  ownershipBp: number;
  /** Vahvistetut vuokramaksut maksupäivän mukaan, koko asunnon summa. */
  incomeCents: number;
  /** Verovuoden kulut sekä aiempien vuosien perusparannukset ja kalusteet, koko summina. */
  expenses: TaxExpense[];
  capitalChargeIncome: boolean;
  openings?: DepreciationOpeningValues;
}

interface YearAmount {
  year: number;
  amountCents: number;
  description?: string;
}

/** Poistettavan erän tilanne verovuonna (kalusteluettelo ja perusparannukset erittelyssä). */
export interface DepreciationRow {
  description: string;
  year: number;
  amountCents: number;
  depreciationCents: number;
  remainingCents: number;
  /** Ennen Vuokranttia alkanut (alkuarvo). */
  opening: boolean;
}

/**
 * Perusparannusten tasapoisto eräkohtaisesti: 1/10 vuodessa maksuvuodesta alkaen. Viimeisenä vuonna
 * vähennetään pyöristyksistä jäänyt loppu, jotta poistot ovat yhteensä tasan hankintameno.
 * Mukana vain erät, joiden poistoaika on käynnissä verovuonna.
 */
export function improvementSchedule(
  year: number,
  items: Array<YearAmount & { opening?: boolean }>,
): DepreciationRow[] {
  return items
    .filter((i) => year - i.year >= 0 && year - i.year < IMPROVEMENT_YEARS)
    .map((i) => {
      const k = year - i.year; // 0 = maksuvuosi
      const annual = Math.round(i.amountCents / IMPROVEMENT_YEARS);
      const isLast = k === IMPROVEMENT_YEARS - 1;
      return {
        description: i.description ?? '',
        year: i.year,
        amountCents: i.amountCents,
        depreciationCents: isLast ? i.amountCents - annual * (IMPROVEMENT_YEARS - 1) : annual,
        remainingCents: isLast ? 0 : i.amountCents - annual * (k + 1),
        opening: i.opening ?? false,
      };
    });
}

export function improvementDepreciation(
  year: number,
  items: YearAmount[],
): { depreciationCents: number; remainingCents: number } {
  const rows = improvementSchedule(year, items);
  return {
    depreciationCents: rows.reduce((n, r) => n + r.depreciationCents, 0),
    remainingCents: rows.reduce((n, r) => n + r.remainingCents, 0),
  };
}

/**
 * Kalusteet: enintään 1 200 euron hankinnat vähennetään kerralla. Kalliimmista poisto tehdään
 * esinekohtaisesti: 25 % esineen poistamattomasta hankintamenosta joka vuosi (enimmäispoisto).
 * Kun esineen poistamaton osa on vuoden alussa enintään 1 200 €, se vähennetään kokonaan.
 * Jokainen kulurivi on yksi esine.
 *
 * Alkuarvo (poistamaton hankintameno vuoden alussa) käsitellään yhtenä esineenä ja korvaa sitä
 * edeltävien vuosien kalustehankinnat, koska se sisältää ne jo.
 *
 * Palauttaa verovuoden kerralla vähennettävät hankinnat ja poistettavien esineiden luettelon.
 */
export function furnitureSchedule(
  year: number,
  items: YearAmount[],
  balance: YearAmount | null = null,
): { immediate: YearAmount[]; assets: DepreciationRow[] } {
  const immediate = items.filter(
    (i) => i.year === year && i.amountCents <= FURNITURE_IMMEDIATE_LIMIT_CENTS,
  );
  const opening = balance && balance.year <= year ? balance : null;
  const assets = items
    .filter(
      (i) =>
        i.year <= year &&
        i.amountCents > FURNITURE_IMMEDIATE_LIMIT_CENTS &&
        (!opening || i.year >= opening.year),
    )
    .map((i) => ({ ...i, opening: false }));
  // Alkuarvo on jo vuoden alun arvo, joten 1 200 euron sääntö koskee sitä heti ensimmäisenä vuonna.
  if (opening) assets.push({ ...opening, opening: true });

  return {
    immediate,
    assets: assets.map((asset) => {
      let remaining = asset.amountCents;
      let depreciation = 0;
      for (let y = asset.year; y <= year; y++) {
        const startOfYear = y > asset.year || asset.opening;
        depreciation =
          startOfYear && remaining <= FURNITURE_IMMEDIATE_LIMIT_CENTS
            ? remaining
            : Math.round(remaining * FURNITURE_RATE);
        remaining -= depreciation;
      }
      return {
        description: asset.description ?? '',
        year: asset.year,
        amountCents: asset.amountCents,
        depreciationCents: depreciation,
        remainingCents: remaining,
        opening: asset.opening,
      };
    }),
  };
}

export function furnitureDepreciation(
  year: number,
  items: YearAmount[],
  balance: YearAmount | null = null,
): { immediateCents: number; depreciationCents: number; remainingCents: number } {
  const { immediate, assets } = furnitureSchedule(year, items, balance);
  return {
    immediateCents: immediate.reduce((n, i) => n + i.amountCents, 0),
    depreciationCents: assets.reduce((n, a) => n + a.depreciationCents, 0),
    remainingCents: assets.reduce((n, a) => n + a.remainingCents, 0),
  };
}

/** Verovuoden poistoerittely: perusparannukset ja kalusteet eräkohtaisesti (koko asunnon summina). */
export function taxSchedules(input: TaxInput): {
  improvements: DepreciationRow[];
  furniture: DepreciationRow[];
  furnitureImmediate: YearAmount[];
} {
  const of = (category: ExpenseCategory) =>
    input.expenses
      .filter((e) => e.category === category)
      .map((e) => ({ year: e.year, amountCents: e.amountCents, description: e.description }));
  const furniture = furnitureSchedule(
    input.year,
    of('furniture'),
    input.openings?.furnitureBalance ?? null,
  );
  return {
    improvements: improvementSchedule(input.year, [
      ...(input.openings?.improvements ?? []).map((i) => ({ ...i, opening: true })),
      ...of('improvement'),
    ]),
    furniture: furniture.assets,
    furnitureImmediate: furniture.immediate,
  };
}

/**
 * Veroilmoituksen luvut yhdelle asunnolle lomakkeen 7H kohtien mukaan (OmaVero: Vuokratulot),
 * omistusosuudella laskettuina. Kohta 2.5 on osiensa summa, jotta erittely täsmää.
 */
export function computeTaxLines(input: TaxInput): TaxLines {
  const { year, ownershipBp } = input;
  const share = (cents: number) => shareOfCents(cents, ownershipBp);
  const sum = (...categories: ExpenseCategory[]) =>
    input.expenses
      .filter((e) => e.year === year && categories.includes(e.category))
      .reduce((n, e) => n + e.amountCents, 0);
  const schedules = taxSchedules(input);
  const total = (rows: DepreciationRow[], key: 'depreciationCents' | 'remainingCents') =>
    rows.reduce((n, r) => n + r[key], 0);
  const improvement = {
    depreciationCents: total(schedules.improvements, 'depreciationCents'),
    remainingCents: total(schedules.improvements, 'remainingCents'),
  };
  const furniture = {
    immediateCents: schedules.furnitureImmediate.reduce((n, i) => n + i.amountCents, 0),
    depreciationCents: total(schedules.furniture, 'depreciationCents'),
    remainingCents: total(schedules.furniture, 'remainingCents'),
  };
  const capital = sum('capital_charge');

  const parts = {
    insurance: share(sum('insurance')),
    otherCosts: share(sum('other')),
    improvementDepreciation: share(improvement.depreciationCents),
    furnitureImmediate: share(furniture.immediateCents),
    furnitureDepreciation: share(furniture.depreciationCents),
  };

  return {
    income: share(input.incomeCents),
    maintenanceAndWater: share(sum('maintenance_charge', 'water')),
    capitalCharges: input.capitalChargeIncome ? share(capital) : 0,
    repairs: share(sum('repair')),
    other: Object.values(parts).reduce((n, c) => n + c, 0),
    ...parts,
    interest: share(sum('interest')),
    capitalChargesNotDeducted: input.capitalChargeIncome ? 0 : share(capital),
    improvementRemaining: share(improvement.remainingCents),
    furnitureRemaining: share(furniture.remainingCents),
  };
}

/** Miten yksittäinen kulu käsitellään verotuksessa. Näytetään kulua kirjattaessa. */
export type ExpenseTaxTreatment =
  | { kind: 'immediate' }
  | { kind: 'improvement'; annualCents: number }
  | { kind: 'furniturePool'; firstYearCents: number }
  | { kind: 'capitalCharge' }
  | { kind: 'interest' };

export function expenseTaxTreatment(
  category: ExpenseCategory,
  amountCents: number,
): ExpenseTaxTreatment {
  switch (category) {
    case 'improvement':
      return { kind: 'improvement', annualCents: Math.round(amountCents / IMPROVEMENT_YEARS) };
    case 'furniture':
      return amountCents > FURNITURE_IMMEDIATE_LIMIT_CENTS
        ? { kind: 'furniturePool', firstYearCents: Math.round(amountCents * FURNITURE_RATE) }
        : { kind: 'immediate' };
    case 'capital_charge':
      return { kind: 'capitalCharge' };
    case 'interest':
      return { kind: 'interest' };
    default:
      return { kind: 'immediate' };
  }
}

/**
 * Remontin ajoitus suhteessa vuokraukseen. Ennen ensimmäistä vuokrausta tehty remontti lisätään
 * hankintamenoon; vuokrauksen päätyttyä vähennyskelpoista on vain vuokrauksesta johtuva osa.
 * Vuokralaisten välinen aika on vuokrauskäyttöä.
 */
export function rentalTiming(
  date: string,
  leases: Array<{ start: string; end: string | null }>,
): 'noLease' | 'before' | 'after' | null {
  if (leases.length === 0) return 'noLease';
  if (date < leases.reduce((m, l) => (l.start < m ? l.start : m), leases[0]!.start)) {
    return 'before';
  }
  if (leases.every((l) => l.end !== null)) {
    const lastEnd = leases.reduce((m, l) => (l.end! > m ? l.end! : m), leases[0]!.end!);
    if (date > lastEnd) return 'after';
  }
  return null;
}
